import React, { useState } from 'react';
import { createUseStyles } from 'react-jss';
import { gql, useApolloClient, useMutation, useQuery } from '@apollo/client';
import { useDispatch } from 'react-redux';
import moment from 'moment';
import ConfirmationModal from '~/app/components/ConfirmationModal';
import { flashMessage } from '~/app/actions';
import { S, labelStyle } from './shellTokens';

export const PAGE_HISTORY = gql`
  query PageHistory($pageId: ID!, $limit: Int, $before: String) {
    pageHistory(pageId: $pageId, limit: $limit, before: $before) { kind at who version text }
  }
`;

const REVERT_PAGE = gql`
  mutation revertPage($pageId: String!, $version: String) {
    revertPage(pageId: $pageId, version: $version) { _id }
  }
`;

const PAGE_SIZE = 40;
// Page analytics are recorded from this date (see the page analytics view).
const ANALYTICS_FROM = Date.UTC(2026, 0, 9, 0, 0, 0);

export type HistoryItem = { kind: string; at: string; who: string | null; version: string | null; text: string | null };

type Props = {
  pageId: string;
  publishedVersion?: string | null;
  canRevert?: boolean;
  canPublish?: boolean;
  /** The draft on the canvas goes into undo before a restore. */
  restoreKeepsDraft?: boolean;
  /** Called before the restore is sent: the restored version replaces the draft, unsaved edits included. */
  onBeforeRestore?: () => void | Promise<void>;
  onRestoreFailed?: () => void;
  onRestored?: (version: string) => void;
  /** Opens the publish sheet for a saved version. */
  onPublish: (version: string) => void;
};

const WHAT: Record<string, (i: HistoryItem) => string> = {
  version: i => `Saved version ${i.version}`,
  committed: i => `Committed version ${i.version || ''}`.trim(),
  saved: () => 'Saved the draft',
  'review-requested': i => `Asked for review${i.version ? ` of ${i.version}` : ''}`,
  approved: i => `Approved${i.version ? ` ${i.version}` : ''}`,
  declined: i => `Declined${i.version ? ` ${i.version}` : ''}`,
  deployed: i => `Went live${i.version ? ` (${i.version})` : ''}`,
  'rolled-back': i => `Rolled back${i.version ? ` to ${i.version}` : ''}`
};

const LAMP: Record<string, string> = {
  deployed: S.green, approved: S.green, declined: S.red, 'rolled-back': S.amber, 'review-requested': S.amber
};

const keyOf = (i: HistoryItem) => `${i.kind}|${i.at}|${i.version || ''}|${i.who || ''}`;

/** Appends an older page, dropping items already shown (the server repeats those exactly at the boundary). */
export const appendOlder = (shown: HistoryItem[], older: HistoryItem[]): HistoryItem[] => {
  const seen = new Set(shown.map(keyOf));
  return shown.concat(older.filter(i => !seen.has(keyOf(i))));
};

const useStyles = createUseStyles({
  root: { display: 'grid', gap: 4, fontSize: 13, color: S.fg },
  empty: { color: S.muted, fontSize: 13, padding: '8px 0' },
  // Saves, reviews and deployments: compact, nothing to click.
  event: { display: 'grid', gridTemplateColumns: '10px minmax(0, 1fr)', gap: 10, padding: '7px 8px' },
  lamp: { width: 8, height: 8, borderRadius: '50%', marginTop: 5, background: S.fieldLine },
  what: { fontWeight: 600 },
  meta: { color: S.muted, fontSize: 12, marginTop: 2 },
  note: { marginTop: 3, color: S.fg, overflowWrap: 'anywhere' },
  // A version: a card you open to see what you can do with it.
  card: {
    borderRadius: 8,
    border: '1px solid transparent',
    transition: 'background 120ms ease, border-color 120ms ease',
    '&:hover': { background: S.chromeHover }
  },
  cardOpen: {
    background: S.field,
    borderColor: S.fieldLine,
    boxShadow: '0 1px 3px rgba(23, 26, 32, 0.08)',
    '&:hover': { background: S.field }
  },
  cardHead: {
    all: 'unset',
    boxSizing: 'border-box',
    cursor: 'pointer',
    width: '100%',
    display: 'grid',
    gridTemplateColumns: '10px minmax(0, 1fr) auto',
    gap: 10,
    padding: '9px 8px',
    borderRadius: 8,
    '&:focus-visible': { boxShadow: `inset 0 0 0 2px ${S.fg}` }
  },
  versionLine: { display: 'flex', alignItems: 'center', gap: 8 },
  version: { fontFamily: S.mono, fontSize: 13, fontWeight: 500 },
  live: { fontFamily: S.label, fontSize: 10, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: S.green, border: `1px solid ${S.green}`, borderRadius: 3, padding: '0 5px', lineHeight: '15px' },
  versionNote: { marginTop: 3, color: S.fg, overflowWrap: 'anywhere', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' },
  noNote: { marginTop: 3, color: S.muted, fontStyle: 'italic' },
  chevron: { color: S.muted, fontSize: 20, alignSelf: 'center', transition: 'transform 160ms ease', '@media (prefers-reduced-motion: reduce)': { transition: 'none' } },
  chevronOpen: { transform: 'rotate(180deg)' },
  actions: { padding: '2px 6px 8px', borderTop: `1px solid ${S.line}` },
  group: { ...labelStyle, fontSize: 10, padding: '10px 4px 4px' },
  // Each action is a full-width row with what it does underneath: big, separate targets.
  action: {
    all: 'unset',
    boxSizing: 'border-box',
    cursor: 'pointer',
    width: '100%',
    display: 'grid',
    gridTemplateColumns: '28px minmax(0, 1fr) auto',
    alignItems: 'center',
    gap: 8,
    padding: '8px 6px',
    borderRadius: 6,
    textDecoration: 'none',
    color: S.fg,
    '& .material-icons': { fontSize: 19, color: S.muted },
    '&:hover, &:focus-visible': { background: S.chromeHover, '& .material-icons': { color: S.fg } },
    '&:focus-visible': { boxShadow: `inset 0 0 0 2px ${S.fg}` }
  },
  actionLabel: { display: 'block', fontSize: 13, fontWeight: 600 },
  actionHint: { display: 'block', fontSize: 12, color: S.muted, marginTop: 1 },
  external: { fontSize: '15px !important' },
  more: {
    all: 'unset',
    cursor: 'pointer',
    margin: '12px 0 4px',
    fontSize: 13,
    fontWeight: 600,
    textAlign: 'center',
    padding: '7px 0',
    border: `1px solid ${S.fieldLine}`,
    borderRadius: 6,
    background: S.field,
    '&:hover': { borderColor: S.fg },
    '&:disabled': { color: S.muted, cursor: 'default' }
  }
});

const HistoryPanel: React.FC<Props> = ({
  pageId, publishedVersion, canRevert, canPublish, restoreKeepsDraft, onBeforeRestore, onRestoreFailed, onRestored, onPublish
}) => {
  const c = useStyles();
  const client = useApolloClient();
  const dispatch = useDispatch();
  const flash = (message: string, type?: string) => dispatch(flashMessage(message, type));
  const { data, loading, error } = useQuery<{ pageHistory: HistoryItem[] }>(PAGE_HISTORY, {
    variables: { pageId, limit: PAGE_SIZE },
    fetchPolicy: 'cache-and-network'
  });
  const [older, setOlder] = useState<HistoryItem[]>([]);
  const [olderDone, setOlderDone] = useState(false);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [confirm, setConfirm] = useState<{ action: 'restore'; version: string } | null>(null);
  // One version open at a time, so its actions never sit next to another version's.
  const [open, setOpen] = useState<string | null>(null);
  const [revertPage] = useMutation(REVERT_PAGE, { refetchQueries: ['getPageEditor', 'pageVersionSummaries', 'PageHistory'] });

  if (loading && !data) return <div className={c.empty}>Loading history…</div>;
  if (error && !data) return <div className={c.empty}>Couldn’t load the history: {error.message}</div>;
  const first = (data && data.pageHistory) || [];
  const items = appendOlder(first, older);
  if (!items.length) return <div className={c.empty}>Nothing yet. Versions, reviews and deployments of this page appear here.</div>;
  const mayHaveMore = !olderDone && (older.length ? true : first.length >= PAGE_SIZE);

  const loadOlder = async () => {
    setLoadingOlder(true);
    try {
      const last = items[items.length - 1];
      const r = await client.query<{ pageHistory: HistoryItem[] }>({
        query: PAGE_HISTORY,
        variables: { pageId, limit: PAGE_SIZE, before: last.at },
        fetchPolicy: 'network-only'
      });
      const next = appendOlder(items, r.data.pageHistory);
      if (r.data.pageHistory.length < PAGE_SIZE || next.length === items.length) setOlderDone(true);
      // Keep everything shown so far, not just what follows the first page: a refetch
      // (after a save) shifts the first page, and items at its old end would drop out.
      setOlder(next);
    } catch (e) {
      flash((e as Error).message, 'ERROR');
    } finally {
      setLoadingOlder(false);
    }
  };

  const restore = async (version: string) => {
    setConfirm(null);
    if (onBeforeRestore) await onBeforeRestore();
    try {
      const r = await revertPage({ variables: { pageId, version } });
      // The server answers null when it couldn't write the page.
      if (!r.data || !r.data.revertPage) throw new Error('The restore didn’t go through. Try again.');
    } catch (e) {
      if (onRestoreFailed) onRestoreFailed();
      flash((e as Error).message, 'ERROR');
      return;
    }
    flash(`Restored version ${version} into your draft.${restoreKeepsDraft ? ' Undo (⌘Z) brings back what you had.' : ''}`);
    if (onRestored) onRestored(version);
  };

  return (
    <div className={c.root} data-testid="history-panel">
      {items.map((i) => {
        const when = <span title={new Date(i.at).toLocaleString()}>{moment(i.at).fromNow()}{i.who ? ` · ${i.who}` : ''}</span>;
        if (i.kind !== 'version' || !i.version) {
          return (
            <div key={keyOf(i)} className={c.event} data-kind={i.kind} data-version={i.version || undefined}>
              <span className={c.lamp} style={LAMP[i.kind] ? { background: LAMP[i.kind] } : undefined} />
              <div>
                <div className={c.what}>{(WHAT[i.kind] || (() => i.kind))(i)}</div>
                <div className={c.meta}>{when}{i.kind === 'deployed' || i.kind === 'rolled-back' ? ` · ${i.text}` : ''}</div>
                {i.text && i.kind !== 'deployed' && i.kind !== 'rolled-back' && <div className={c.note}>{i.text}</div>}
              </div>
            </div>
          );
        }
        const version = i.version;
        const isLive = version === publishedVersion;
        const isOpen = open === keyOf(i);
        const panelId = `history-actions-${version}`;
        const hasAnalytics = Date.parse(i.at) >= ANALYTICS_FROM;
        return (
          <div key={keyOf(i)} className={`${c.card} ${isOpen ? c.cardOpen : ''}`} data-kind="version" data-version={version}>
            <button type="button" className={c.cardHead} aria-expanded={isOpen} aria-controls={panelId} onClick={() => setOpen(isOpen ? null : keyOf(i))} data-version-toggle>
              <span className={c.lamp} style={isLive ? { background: S.green } : { background: S.fg }} />
              <span>
                <span className={c.versionLine}>
                  <span className={c.version}>v{version}</span>
                  {isLive && <span className={c.live} data-live>Live</span>}
                </span>
                {i.text ? <span className={c.versionNote} style={{ display: '-webkit-box' }}>{i.text}</span> : <span className={c.noNote} style={{ display: 'block' }}>No note</span>}
                <span className={c.meta} style={{ display: 'block' }}>{when}</span>
              </span>
              <i className={`material-icons ${c.chevron} ${isOpen ? c.chevronOpen : ''}`} aria-hidden>expand_more</i>
            </button>
            {isOpen && (
              <div className={c.actions} id={panelId} role="group" aria-label={`Version ${version}`}>
                <div className={c.group}>Look</div>
                <a className={c.action} href={`/admin/page/${pageId}/preview/${version}`} target="_blank" rel="noopener noreferrer" data-action="preview">
                  <i className="material-icons" aria-hidden>visibility</i>
                  <span><span className={c.actionLabel}>Preview</span><span className={c.actionHint}>Opens this version in a new tab</span></span>
                  <i className={`material-icons ${c.external}`} aria-hidden>open_in_new</i>
                </a>
                {hasAnalytics && (
                  <a className={c.action} href={`/admin/page/${pageId}/analytics?version=${version}`} data-action="analytics">
                    <i className="material-icons" aria-hidden>insights</i>
                    <span><span className={c.actionLabel}>Analytics</span><span className={c.actionHint}>How this version did while it was live</span></span>
                    <span />
                  </a>
                )}
                {(canRevert || (canPublish && !isLive)) && <div className={c.group}>Change</div>}
                {canRevert && (
                  <button type="button" className={c.action} onClick={() => setConfirm({ action: 'restore', version })} data-action="restore">
                    <i className="material-icons" aria-hidden>settings_backup_restore</i>
                    <span>
                      <span className={c.actionLabel}>Restore into draft</span>
                      <span className={c.actionHint}>{restoreKeepsDraft ? 'Replaces your draft; Undo brings it back' : 'Replaces the draft with this version'}</span>
                    </span>
                    <span />
                  </button>
                )}
                {canPublish && !isLive && (
                  <button type="button" className={c.action} onClick={() => onPublish(version)} data-action="publish">
                    <i className="material-icons" aria-hidden>publish</i>
                    <span><span className={c.actionLabel}>Publish…</span><span className={c.actionHint}>Ask for review of this version</span></span>
                    <span />
                  </button>
                )}
              </div>
            )}
          </div>
        );
      })}
      {mayHaveMore && (
        <button type="button" className={c.more} onClick={loadOlder} disabled={loadingOlder}>
          {loadingOlder ? 'Loading…' : 'Show older'}
        </button>
      )}
      {confirm && confirm.action === 'restore' && (
        <ConfirmationModal
          message={restoreKeepsDraft
            ? `Restore version ${confirm.version} into your draft? It replaces what’s in the draft now, and Undo (⌘Z) brings that back. Published pages don’t change until you publish.`
            : `Restore version ${confirm.version} into your draft? This replaces the current draft content. Published pages don’t change until you publish.`}
          actionLabel="Restore"
          onClick={() => restore(confirm.version)}
          onClose={() => setConfirm(null)}
        />
      )}
    </div>
  );
};

export default HistoryPanel;
